import { useQuery } from '@tanstack/react-query';
import { objectiveAnalyticsApi } from '../lib/api-client';

// Cache configuration constants
const FIVE_MINUTES = 5 * 60 * 1000;
const TEN_MINUTES = 10 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

/**
 * Hook for fetching original objective analytics data
 * Used for the old objective analytics view with trends
 */
export const useObjectiveAnalytics = (params?: { locationId?: string; numberOfWeeks?: number }) => {
  return useQuery({
    queryKey: ['objective-analytics', params],
    queryFn: () => objectiveAnalyticsApi.getObjectives(params),
    staleTime: FIVE_MINUTES, // Data considered fresh for 5 minutes
    gcTime: TEN_MINUTES, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry failed requests twice
  });
};

/**
 * Hook for fetching standings (status distribution) per objective
 * Returns: { total, data: [{ label, value }] }
 *
 * @param objective - The objective enum value (e.g., 'STANDARD_HOUSE')
 */
export const useStandingsPerObjective = (params: {
  objective: string;
  weekNumber?: number;
  year?: number;
  month?: string;
  startDate?: string;
  endDate?: string;
  facilityTypeId?: string;
  geoLocationId?: string;
}) => {
  return useQuery({
    queryKey: ['standings-per-objective', params],
    queryFn: () => objectiveAnalyticsApi.getStandingsPerObjective(params),
    enabled: !!params.objective,
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for fetching facilities with their requirement status
 * Returns detailed facility list with availability status
 *
 * @param params.objective - The objective enum value
 * @param params.locationId - Optional location ID for filtering
 */
export const useFacilitiesRequirementStatus = (params: {
  objective: string;
  locationId?: string;
  facilityTypeId?: string;
  weekNumber?: number;
  year?: number;
  month?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['facilities-requirement-status', params],
    queryFn: () => objectiveAnalyticsApi.getFacilitiesRequirementStatus(params),
    enabled: !!params.objective,
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook for fetching location rankings by objective
 * Supports drill-down navigation through location hierarchy
 *
 * @param params.objective - The objective enum value
 * @param params.parentLocationId - Optional parent location for drill-down
 */
export const useLocationRankingByObjective = (params: {
  objective: string;
  parentLocationId?: string;
  weekNumber?: number;
  year?: number;
  facilityTypeId?: string;
}) => {
  return useQuery({
    queryKey: ['location-ranking-by-objective', params],
    queryFn: () => objectiveAnalyticsApi.getLocationRanking(params),
    enabled: !!params.objective,
    staleTime: TEN_MINUTES,
    gcTime: THIRTY_MINUTES,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};
